import React, { useState, useEffect, useCallback } from 'react';
import './Dashboard.css';
import RankingFluctuationAfterHours from '../features/RTRankingFluctuationAfterHours';
import BacktestingChart from '../features/BTRankingFluctuationDaily';
import AfterHoursBacktestingChart from '../features/BTRankingFluctuationAfterHours';
import RealtimeRankingFluctuation from '../features/RTRankingFluctuationDaily';
import { useRealtimeRankingFluctuationDaily } from '../context/RealtimeRankingFluctuationContext';
import { useWebSocket } from '../hooks/useWebSocket';

interface TradeStockRecord {
  id: number;
  trade_type: string;
  stock_code: string;
  stock_name: string;
  order_quantity: string;
  order_price: string;
  price: number;
  change_rate: number;
  volume: number;
  collected_at: string;
  trade_timestamp: string;
}

const StockDashboard: React.FC = () => {
  // 환경 변수 확인 (프로덕션에서는 백테스트 섹션 숨김)
  const isProduction = process.env.REACT_APP_ENV === 'mercury-frontend-prod';
  
  // 급상승탐지 ON/OFF 상태
  const [surgeDetectionEnabled, setSurgeDetectionEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [, setTradeStockRecords] = useState<TradeStockRecord[]>([]);

  // KRX 차트: 실시간 등락률 데이터 사용
  useRealtimeRankingFluctuationDaily();

  // Kiwoom Account: mercury-backend trade-stock WebSocket (trade_stock 타입용)
  // trade_stock_history는 mercury-stock이 rt-ranking-fluctuation-daily와 동일 WebSocket으로 전송 → context에서 수신
  const tradeStockWsUrl = process.env.NODE_ENV === 'development'
    ? 'ws://localhost:8080/backtesting/trade-stock'
    : '/mercury-backend/backtesting/trade-stock';
  useWebSocket({
    url: tradeStockWsUrl,
    reconnectInterval: 5000,
    reconnectLimit: 3,
    onMessage: useCallback((message: { type?: string; data?: TradeStockRecord[] }) => {
      const data = message.data;
      if (message.type === 'trade_stock' && Array.isArray(data)) {
        setTradeStockRecords((prev) => {
          const newRecords = [...(data as TradeStockRecord[]), ...prev];
          const uniqueRecords = newRecords.filter((record, index, self) =>
            index === self.findIndex((r) => r.id === record.id)
          );
          return uniqueRecords.slice(0, 100);
        });
      }
    }, []),
  });

  // 페이지 로드 시 Redis 상태 확인
  useEffect(() => {
    const checkRedisStatus = async () => {
      try {
        const response = await fetch(`/mercury-backend/settings/surge-detection?action=status`);
        if (response.ok) {
          const data = await response.json();
          const isEnabled = data.data?.is_enabled || false;
          setSurgeDetectionEnabled(isEnabled);
          console.log('🔄 Redis 상태 동기화:', isEnabled ? 'ON' : 'OFF');
        }
      } catch (error) {
        console.error('❌ Redis 상태 확인 오류:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    checkRedisStatus();
  }, []);

  // 급상승탐지 설정만 관리 (초기화 완료 후에만 실행)
  useEffect(() => {
    if (!isInitialized) return; // 초기화가 완료되지 않았으면 실행하지 않음

    if (surgeDetectionEnabled) {
      // 설정 활성화
      (async () => {
        try {
          const response = await fetch(`/mercury-backend/settings/surge-detection?action=enable`);
          if (response.ok) {
            console.log('✅ 급상승탐지 전략 활성화 완료');
          }
        } catch (error) {
          console.error('❌ 급상승탐지 전략 활성화 오류:', error);
        }
      })();
    } else {
      // 설정 비활성화
      (async () => {
        try {
          const response = await fetch(`/mercury-backend/settings/surge-detection?action=disable`);
          if (response.ok) {
            console.log('✅ 급상승탐지 전략 비활성화 완료');
          }
        } catch (error) {
          console.error('❌ 급상승탐지 전략 비활성화 오류:', error);
        }
      })();
    }
  }, [surgeDetectionEnabled, isInitialized]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-sections">
        {/* 등락률 백테스트 섹션 - 가장 위 */}
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h2>등락률 백테스트</h2>
          </div>
          <div className="section-content">
            {isProduction ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 프로덕션 환경에서는 이용할 수 없습니다
                </p>
              </div>
            ) : (
              <BacktestingChart />
            )}
          </div>
        </div>

        {/* KRX + Kiwoom Account - 일시적으로 비활성화 */}
        <div className="dashboard-section full-width" style={{ marginTop: '30px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '30px',
            alignItems: 'stretch',
            width: '100%',
            maxWidth: 1500,
            margin: '0 auto',
          }}>
            <section style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}><h2>KRX</h2></div>
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 일시적으로 비활성화 되었습니다
                </p>
              </div>
            </section>
            <section style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="section-header" style={{ marginBottom: '20px' }}><h2>Kiwoom Account</h2></div>
              <div style={{
                padding: '20px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                color: '#6c757d'
              }}>
                <p style={{ margin: 0, fontSize: '16px' }}>
                  🚫 일시적으로 비활성화 되었습니다
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* 실시간 등락률 | 실시간 시간외 등락률 | 시간외 등락률 백테스트 - 나란히 */}
        <div className="dashboard-section-row">
          <div className="dashboard-section">
            <div className="section-header">
              <h2>실시간 등락률</h2>
            </div>
            <div className="section-content">
              <RealtimeRankingFluctuation />
              <div style={{
                width: '100%',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                padding: '16px',
                fontSize: '12px',
                color: '#e6edf3',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ color: '#fbbf24' }}>급상승탐지</div>
                <div
                  onClick={() => setSurgeDetectionEnabled(!surgeDetectionEnabled)}
                  style={{
                    width: '50px',
                    height: '24px',
                    background: surgeDetectionEnabled ? '#10b981' : '#6b7280',
                    borderRadius: '12px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <div
                    style={{
                      width: '20px',
                      height: '20px',
                      background: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: surgeDetectionEnabled ? '28px' : '2px',
                      transition: 'left 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-section">
            <div className="section-header">
              <h2>실시간 시간외 등락률</h2>
            </div>
            <div className="section-content">
              {!isProduction ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  color: '#6c757d'
                }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    🚫 로컬 환경에서는 이용할 수 없습니다
                  </p>
                </div>
              ) : (
                <RankingFluctuationAfterHours />
              )}
            </div>
          </div>
          <div className="dashboard-section">
            <div className="section-header">
              <h2>시간외 등락률 백테스트</h2>
            </div>
            <div className="section-content">
              {isProduction ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  color: '#6c757d'
                }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    🚫 프로덕션 환경에서는 이용할 수 없습니다
                  </p>
                </div>
              ) : (
                <AfterHoursBacktestingChart />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockDashboard;

